import { reportGqlError, request, useQuery } from '../../api'
import {
  parseDatesInEmployeeSalariesItemsGqlResponse,
  parseDatesInEmployeesFullSalaryInfoGqlResponse,
  parseDatesInFullSalaryInfoGqlResponse,
} from './logic'
import { SALARY_ISSUES_QUERY_KEY } from '../salaryIssue'
import { TRANSACTIONS_QUERY_KEY } from '../transaction'
import { gql } from 'graphql-request'
import {
  SALARY_EMPLOYEES_FULL_INFO_QUERY_KEY,
  SALARY_EMPLOYEES_ITEMS_QUERY_KEY,
  SALARY_FULL_INFO_QUERY_KEY,
} from './queryKeys'
import {
  GetEmployeeSalariesItemsCloudFunctionResult,
  GetEmployeesSalaryFullInfoCloudFunctionResult,
  GetSalaryFullInfoCloudFunctionResult,
} from './types'
import { DEFAULT_TIMEZONE } from '@expane/date'

export function useFetchSalaryFullInfo({
  from,
  to,
  timezone,
  branchId,
}: {
  from: Date
  to: Date
  timezone: string | undefined
  branchId: number | undefined
}) {
  return useQuery<GetSalaryFullInfoCloudFunctionResult>(
    [
      SALARY_FULL_INFO_QUERY_KEY,
      TRANSACTIONS_QUERY_KEY,
      SALARY_ISSUES_QUERY_KEY,
      { from, to, branchId },
    ],

    async () => {
      const result = await request(
        gql`
          query ($from: timestamptz!, $to: timestamptz!, $branchId: Int!) {
            getSalaryFullInfo(branchId: $branchId, fromDate: $from, toDate: $to) {
              code
              message
              data
            }
          }
        `,
        { from, to, branchId },
      )

      if (result && Array.isArray(result.getSalaryFullInfo.data)) {
        return {
          ...result.getSalaryFullInfo,
          data: result.getSalaryFullInfo.data.map(item =>
            parseDatesInFullSalaryInfoGqlResponse(item, timezone || DEFAULT_TIMEZONE),
          ),
        }
      }
      return result
    },
    {
      queryName: 'useFetchSalaryFullInfo',
      onError: reportGqlError,
      enabled: Boolean(timezone) && Boolean(branchId),
    },
  )
}

export function useFetchEmployeesSalaryFullInfo({
  from,
  to,
  employeeIds,
  timezone,
  branchId,
}: {
  from: Date
  to: Date
  employeeIds: number[] | undefined
  timezone: string | undefined
  branchId: number | undefined
}) {
  return useQuery<GetEmployeesSalaryFullInfoCloudFunctionResult>(
    [
      SALARY_EMPLOYEES_FULL_INFO_QUERY_KEY,
      TRANSACTIONS_QUERY_KEY,
      SALARY_ISSUES_QUERY_KEY,
      { from, to, branchId, employeeIds },
    ],

    async () => {
      const result = await request(
        gql`
          query ($from: timestamptz!, $to: timestamptz!, $employeeIds: [Int!]!, $branchId: Int!) {
            getEmployeesSalaryFullInfo(
              employeeIds: $employeeIds
              branchId: $branchId
              fromDate: $from
              toDate: $to
            ) {
              code
              message
              data
            }
          }
        `,
        { from, to, branchId, employeeIds },
      )

      if (result && Array.isArray(result.getEmployeesSalaryFullInfo.data)) {
        return {
          ...result.getEmployeesSalaryFullInfo,
          data: result.getEmployeesSalaryFullInfo.data.map(item =>
            parseDatesInEmployeesFullSalaryInfoGqlResponse(item, timezone || DEFAULT_TIMEZONE),
          ),
        }
      }
      return result
    },
    {
      queryName: 'useFetchEmployeesSalaryFullInfo',
      onError: reportGqlError,
      enabled: Boolean(timezone) && Boolean(branchId) && Boolean(employeeIds),
    },
  )
}

export function useFetchEmployeeSalariesItems({
  from,
  to,
  employeeId,
  timezone,
  branchId,
}: {
  from: Date
  to: Date
  employeeId?: number | null
  timezone: string | undefined
  branchId: number | undefined
}) {
  return useQuery<GetEmployeeSalariesItemsCloudFunctionResult>(
    [
      SALARY_EMPLOYEES_ITEMS_QUERY_KEY,
      TRANSACTIONS_QUERY_KEY,
      SALARY_ISSUES_QUERY_KEY,
      { from, to, branchId, employeeId },
    ],

    async () => {
      const result = await request(
        gql`
          query ($from: timestamptz!, $to: timestamptz!, $employeeId: Int!, $branchId: Int!) {
            getEmployeeSalariesItems(
              employeeId: $employeeId
              branchId: $branchId
              fromDate: $from
              toDate: $to
            ) {
              code
              message
              data
            }
          }
        `,
        { from, to, branchId, employeeId },
      )

      if (
        result &&
        Array.isArray(result.getEmployeeSalariesItems.data.salaryItems) &&
        Array.isArray(result.getEmployeeSalariesItems.data.referralPercentageSalaryItems)
      ) {
        return {
          ...result,
          data: parseDatesInEmployeeSalariesItemsGqlResponse(
            result.getEmployeeSalariesItems.data,
            timezone || DEFAULT_TIMEZONE,
          ),
        }
      }
      return result
    },
    {
      queryName: 'useFetchEmployeeSalariesItems',
      onError: reportGqlError,
      enabled: Boolean(timezone) && Boolean(branchId) && Boolean(employeeId),
    },
  )
}
